import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link, useNavigate} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../context/AuthContext';
import { trackEvent, EVENTS } from '../utils/analytics';
import config from '../config/config';
import StockCard from '../components/StockCard';
import PerformanceChart from './PerformanceChart';
import PortfolioGrowthChart from './PortfolioGrowthChart';
import { formatNumber, formatCurrencyValue, formatBillionValue } from '../utils/formatters';
import '../styles/StockDetail.css';

const StockDetail = () => {
  const { id } = useParams();
  const { token, getAuthHeaders } = useContext(AuthContext);
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [timeframe, setTimeframe] = useState('year');
  const [preferredCurrency, setPreferredCurrency] = useState('EUR');
  const [showMovingAverages, setShowMovingAverages] = useState(false);
  const navigate = useNavigate();

  const formatDividendHistoryForChart = (dividendHistory) => {
    if (!dividendHistory || !Array.isArray(dividendHistory) || dividendHistory.length === 0) {
      return [];
    }
  
    // Sort by ex_date in ascending order
    const sortedDividends = [...dividendHistory].sort((a, b) => 
      new Date(a.ex_date) - new Date(b.ex_date)
    );
    
    return sortedDividends.map(dividend => {
      // Format date for display
      const date = new Date(dividend.ex_date);
      const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      // Calculate percentage growth compared to previous dividend
      const prevIndex = sortedDividends.indexOf(dividend) - 1;
      let percentageGrowth = 0;
      if (prevIndex >= 0) {
        const prevAmount = sortedDividends[prevIndex].amount;
        percentageGrowth = ((dividend.amount - prevAmount) / prevAmount) * 100;
      }
      
      return {
        value: dividend.amount,
        period,
        date: dividend.ex_date,
        isPositive: percentageGrowth >= 0, // Will show positive/negative color
        percentageGrowth,
        totalValue: dividend.amount // Required by chart component
      };
    });
  };

  // Fetch history data function wrapped in useCallback
  const fetchHistoryData = useCallback(async (period, stockId, currency) => {
    try {
      const headers = token ? await getAuthHeaders() : { 'Content-Type': 'application/json' };

      // Include moving average indicators in the request
      const indicators = showMovingAverages ? 'ma' : '';
      const url = `${config.apiUrl}/api/stocks/${stockId}/history?period=${period}&currency=${currency}${indicators ? `&indicators=${indicators}` : ''}`;

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`Failed to fetch history data: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setHistoryData(data.data);
      } else {
        setHistoryData([]);
      }
    } catch (err) {
      console.error('Error fetching price history:', err);
      setHistoryData([]);
    }
  }, [token, getAuthHeaders, showMovingAverages]);

  // Fetch stock data when id or token changes
  useEffect(() => {
    const fetchStockData = async () => {
      try {
        setLoading(true);

        // First get preferred currency
        let currency = 'EUR';
        if (token) {
          try {
            const headers = await getAuthHeaders();
            const currencyResponse = await fetch(`${config.apiUrl}/api/portfolio/preferred-currency`, {
              headers
            });
            const currencyData = await currencyResponse.json();
            currency = currencyData.currency;
            setPreferredCurrency(currency);
          } catch (err) {
            console.error('Error fetching preferred currency:', err);
          }
        }

        // Then fetch the stock data using the new GET endpoint
        const headers = token ? await getAuthHeaders() : {};
        const response = await fetch(`${config.apiUrl}/api/stocks/${id}?currency=${currency}`, {
          headers
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch stock data: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.data) {
          setStock(data.data);

          // Track view event
          trackEvent(EVENTS.VIEW_STOCK_DETAIL, {
            stock_id: data.data.id,
            stock_name: data.data.name,
            stock_sector: data.data.sector
          });

          // Fetch price history data
          await fetchHistoryData(timeframe, id, currency);
        } else {
          throw new Error('Stock not found');
        }
      } catch (err) {
        console.error('Error fetching stock data:', err);
        setError(err.message || 'Failed to load stock data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchStockData();
    }
  }, [id, token, getAuthHeaders, fetchHistoryData, timeframe]);

  // Separate effect for handling timeframe changes
  useEffect(() => {
    if (stock && id) {
      fetchHistoryData(timeframe, id, preferredCurrency);
    }
  }, [timeframe, id, preferredCurrency, stock, fetchHistoryData]);

  // Function to handle timeframe change
  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
    
    // Track timeframe change
    if (stock) {
      trackEvent(EVENTS.CHART_PERIOD_CHANGE, {
        stock_id: stock.id,
        period: newTimeframe,
        current_timeframe: timeframe
      });
    }
  };

  // Function to add to portfolio
  const addToPortfolio = () => {
    if (!token) {
      navigate('/login');
      return;
    }
    navigate(`/portfolio?addStock=${stock?.id}`);

    // Implement portfolio modal or redirect to portfolio page
    trackEvent(EVENTS.NAVIGATE_TO_PORTFOLIO, {
      stock_id: stock?.id,
      stock_name: stock?.name,
      from_detail_page: true
    });
    
    // You can navigate to portfolio page with stock pre-selected or show modal
  };

  if (loading) {
    return (
      <div className="stock-detail-loading" role="status">
        <div className="loading-spinner"></div>
        <p>Lade Aktiendetails...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stock-detail-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{error}</p>
        <Link to="/search" className="app-btn app-btn--primary">
          <i className="fas fa-search" aria-hidden="true"></i> Zur Aktiensuche
        </Link>
      </div>
    );
  }

  if (!stock) return null;

  // Get chart data for selected timeframe
  const chartData = historyData;

  // Calculate growth for the current period from chart data
  const periodGrowth = chartData.length >= 2 
  ? ((chartData[chartData.length - 1].price - chartData[0].price) / chartData[0].price) * 100 
  : 0;

  const chartColor = periodGrowth >= 0 
    ? 'var(--success-color)' 
    : 'var(--danger-color)';

  return (
    <>
      <Helmet>
        <title>{`${stock.name} (${stock.symbol || ''}) - Aktienanalyse | DynaStoxx`}</title>
        <meta 
          name="description" 
          content={`Detaillierte Analyse von ${stock.name} - KGV, KBV, Dividendenrendite, Wachstum und mehr. Umfassende Bewertung für Value-Investoren.`} 
        />
        <meta name="keywords" content={`${stock.name}, Aktie, ${stock.sector}, Aktienanalyse, KGV, Wachstumsaktie, Value Investing`} />
        <link rel="canonical" href={`https://dynastoxx.com/stock-detail/${stock.id}`} />
        <meta property="og:title" content={`${stock.name} - Aktienanalyse | DynaStoxx`} />
        <meta property="og:url" content={`https://dynastoxx.com/stock-detail/${stock.id}`} />
      </Helmet>
      <div className="stock-detail-container">
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Start</Link>
          <i className="fas fa-chevron-right" aria-hidden="true"></i>
          <Link to="/search">Aktiensuche</Link>
          <i className="fas fa-chevron-right" aria-hidden="true"></i>
          <span aria-current="page">{stock.name}</span>
        </nav>

        <div className="stock-detail-header">
          <div className="stock-detail-title">
            <h1>{stock.name}</h1>
            <p className="stock-detail-subtitle">
              {[stock.sector, stock.country].filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="header-actions">
            <button 
              type="button"
              className="portfolio-button"
              onClick={addToPortfolio}
            >
              <i className="fas fa-plus" aria-hidden="true"></i>
              {token ? 'Zum Portfolio' : 'Anmelden & hinzufügen'}
            </button>
          </div>
        </div>
        <div className="stock-detail-grid">
          {/* Left column */}
          <div className="detail-left-column">
            {/* Stock info section */}
            <section className="stock-info-section">
              <div className="section-card enhanced-stock-card">
                <StockCard 
                  stock={stock}
                  isPortfolio={false}
                  showActions={false}
                  initialExpanded={true}
                />
              </div>
            </section>
          </div>

          {/* Right column */}
          <div className="detail-right-column">
            {/* Price chart section */}
            <section className="price-chart-section">
              <div className="section-card">
                <div className="section-header">
                  <h2>Kursverlauf</h2>
                  <div className="price-summary">
                    <span className="current-price">{formatCurrencyValue(stock.current_price, stock.display_currency)}</span>
                    <span className={`price-change ${getChangeColorClass(periodGrowth)}`}>
                      <i className={`fas fa-${periodGrowth >= 0 ? 'arrow-up' : 'arrow-down'}`}></i>
                      {formatNumber(Math.abs(periodGrowth))}%
                    </span>
                  </div>
                </div>

                <div className="chart-controls-container">
                  <div className="timeframe-buttons">
                    {[['week', '1W'], ['month', '1M'], ['year', '1J'], ['fiveYear', '5J']].map(([key, label]) => (
                      <button 
                        key={key}
                        type="button"
                        className={`timeframe-button ${timeframe === key ? 'active' : ''}`}
                        onClick={() => handleTimeframeChange(key)}
                        aria-pressed={timeframe === key}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  
                  <div className="chart-options">
                    <label className="moving-averages-toggle">
                      <input 
                        type="checkbox" 
                        checked={showMovingAverages}
                        onChange={(e) => setShowMovingAverages(e.target.checked)}
                      />
                      <span className="checkbox-label">Gleitende Durchschnitte</span>
                    </label>
                  </div>
                </div>

                <div className="chart-container">
                  {chartData.length > 0 ? (
                    <PerformanceChart 
                      data={chartData}
                      dataKey="price"
                      currency={stock.display_currency}
                      chartColor={chartColor}
                      showYAxis={true}
                      showMovingAverages={showMovingAverages}
                    />
                  ) : (
                    <div className="chart-placeholder">
                      <p>Keine Kursdaten verfügbar für diesen Zeitraum</p>
                    </div>
                  )}
                </div>
                
                {showMovingAverages && (
                  <div className="chart-legend">
                    <div className="legend-item">
                      <span className="legend-line main-line" style={{backgroundColor: chartColor}}></span>
                      <span className="legend-label">Kurs</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-line ma50-line"></span>
                      <span className="legend-label">50-Tage-Durchschnitt</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-line ma200-line"></span>
                      <span className="legend-label">200-Tage-Durchschnitt</span>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Dividend History Section */}
            {stock.dividend_history && stock.dividend_history.length > 0 && (
              <section className="dividend-chart-section">
                <div className="section-card">
                  <div className="section-header">
                    <h2>Dividendenhistorie</h2>
                    <div className="price-summary">
                      <span className="current-price">{formatCurrencyValue(stock.dividend, stock.display_currency)}</span>
                      <span className={`price-change positive`}>
                        <i className="fas fa-chart-pie"></i>
                        {formatNumber(stock.dividend_yield)}%
                      </span>
                    </div>
                  </div>

                  <div className="performance-summary">
                    <div className="performance-metric">
                      <span className="metric-label">Dividendenrendite</span>
                      <span className="metric-value">
                        {formatNumber(stock.dividend_yield)}%
                      </span>
                    </div>
                    <div className="performance-metric">
                      <span className="metric-label">Letzte Dividende</span>
                      <span className="metric-value">
                        {stock.dividend_history.length > 0 ? 
                          formatCurrencyValue(stock.dividend_history[0].amount, stock.display_currency) : 'N/A'}
                      </span>
                    </div>
                    <div className="performance-metric">
                      <span className="metric-label">Ausschüttungsquote</span>
                      <span className="metric-value">
                        {formatNumber(stock.payout_ratio)}%
                      </span>
                    </div>
                    <div className="performance-metric">
                      <span className="metric-label">Anzahl Zahlungen</span>
                      <span className="metric-value">
                        {stock.dividend_history.length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="chart-container">
                    <PortfolioGrowthChart 
                      performanceData={formatDividendHistoryForChart(stock.dividend_history)}
                      currency={stock.display_currency}
                      selectedRange="all"
                    />
                  </div>
                  
                  <div className="dividend-table">
                    <h3 className="section-subtitle">Dividendenzahlungen</h3>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>Datum</th>
                            <th>Betrag</th>
                            <th>Änderung</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stock.dividend_history.sort((a, b) => new Date(b.ex_date) - new Date(a.ex_date)).map((dividend, index, array) => {
                            // Calculate year-over-year growth
                            const prevYearDividend = array.find(d => {
                              const currentDate = new Date(dividend.ex_date);
                              const prevDate = new Date(d.ex_date);
                              return prevDate.getFullYear() === currentDate.getFullYear() - 1 &&
                                    Math.abs(prevDate.getMonth() - currentDate.getMonth()) <= 1;
                            });
                            
                            const yoyGrowth = prevYearDividend 
                              ? ((dividend.amount - prevYearDividend.amount) / prevYearDividend.amount) * 100 
                              : null;
                            
                            return (
                              <tr key={dividend.dividend_id || index}>
                                <td>{new Date(dividend.ex_date).toLocaleDateString('de-DE')}</td>
                                <td>{formatCurrencyValue(dividend.amount, stock.display_currency)}</td>
                                <td className={yoyGrowth === null ? '' : yoyGrowth >= 0 ? 'positive' : 'negative'}>
                                  {yoyGrowth !== null ? (
                                    <>
                                      <i className={`fas fa-${yoyGrowth >= 0 ? 'arrow-up' : 'arrow-down'}`}></i>
                                      {Math.abs(yoyGrowth).toFixed(2)}%
                                    </>
                                  ) : '-'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* Company overview section - full width */}
          <section className="company-overview-section">
            <div className="section-card">
              <h2 className="section-title">Über {stock.name}</h2>
              <div className="company-info">
                <div className="info-row">
                  <span className="info-label">Branche</span>
                  <span className="info-value">{stock.sector || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Land</span>
                  <span className="info-value">{stock.country || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Marktkapitalisierung</span>
                  <span className="info-value">
                    {formatBillionValue(stock.market_cap, stock.display_currency)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Umsatz</span>
                  <span className="info-value">
                    {formatBillionValue(stock.revenue, stock.display_currency)}
                  </span>
                </div>
              </div>
              <div className="company-description">
                <p>
                  {stock.name} ist ein {stock.sector} Unternehmen mit Sitz in {stock.country}. 
                  Als Teil der {stock.sector}-Branche bietet das Unternehmen vielversprechende Investitionsmöglichkeiten 
                  für Value-Investoren, die nach soliden Fundamentaldaten suchen.
                </p>
                <p>
                  Mit einer Marktkapitalisierung von {formatBillionValue(stock.market_cap, stock.display_currency)} 
                   und einem KGV von {formatNumber(stock.trailing_p_e)} bietet die Aktie interessante 
                  Kennzahlen für Anleger, die nach {getInvestmentStyle(stock)} suchen.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};


function getChangeColorClass(change) {
  return change >= 0 ? 'positive' : 'negative';
}


function getInvestmentStyle(stock) {
  // Simple logic to determine investment style based on metrics
  const pe = stock.trailing_p_e || 0;
  const divYield = stock.dividend_yield || 0;
  const growthRate = (stock.growth_estimates?.next_year || 0) * 100;
  
  if (divYield > 3.5) return 'Dividendenaktien';
  if (pe < 15 && stock.price_to_book < 2) return 'Value-Aktien';
  if (growthRate > 15) return 'Wachstumsaktien';
  return 'ausgewogene Aktien';
}

export default StockDetail;